import type {Facet, FacetOption} from '../components/facet-panel';

export type ToggleFacetAction = {
  type: 'toggle-facet';
  payload: {id: string};
};

export type ToggleFacetOptionAction = {
  type: 'toggle-facet-option';
  payload: {facetId: string; optionId: string; selected: boolean};
};

export type ApplyFilterAction = {
  type: 'apply-filter';
  payload: {facets: {facetId: string; optionId: string; selected: boolean}[]};
};

export type UpdateFacetsAction = {
  type: 'update-facets';
  payload: {facets: Facet[]};
};

export type UnselectAllOptionsAction = {
  type: 'unselected-all-facet-options';
};

export type ResetAllOptionsAction = {
  type: 'reset-all-facet-options';
  payload: {options: FacetOption[]};
};

type FacetReducerState = {
  facets: Facet[];
  activeFilters: FacetOption[];
};

function disableFacetOption(
  option: FacetOption,
  facet: Facet,
  payloadFacets: Facet[],
) {
  // only disable options that are not selected so you can unselect them
  const disabled =
    !option.selected &&
    !Boolean(
      payloadFacets
        .find((payloadFacet) => payloadFacet.id === facet.id)
        ?.options.find((payloadOption) => payloadOption.id === option.id),
    );
  return {
    ...option,
    disabled,
  };
}

export const facetReducer = (
  state: FacetReducerState,
  action:
    | ToggleFacetAction
    | ToggleFacetOptionAction
    | UpdateFacetsAction
    | UnselectAllOptionsAction
    | ResetAllOptionsAction
    | ApplyFilterAction,
): FacetReducerState => {
  const getActiveFilters = (facets: Facet[]) => {
    return facets.flatMap((facet) =>
      facet.options.filter((option) => option.selected),
    );
  };

  switch (action.type) {
    case `update-facets`: {
      if (state.facets.length <= 0) {
        return {
          ...state,
          facets: action.payload.facets,
          activeFilters: getActiveFilters(action.payload.facets),
        };
      }

      // If payload facets is longer than in state, add those facets
      if (state.facets.length < action.payload.facets.length) {
        const updatedFacets = action.payload.facets.map((facet) => ({
          ...facet,
          options: facet.options.map((option) =>
            disableFacetOption(option, facet, action.payload.facets),
          ),
        }));

        return {
          ...state,
          facets: updatedFacets,
          activeFilters: getActiveFilters(updatedFacets),
        };
      }

      const facets = state.facets.map((facet) => ({
        ...facet,
        options: facet.options.map((option) =>
          disableFacetOption(option, facet, action.payload.facets),
        ),
      }));

      return {...state, facets, activeFilters: getActiveFilters(facets)};
    }

    case `toggle-facet`: {
      const facets = state.facets.map((facet) => {
        if (facet.id !== action.payload.id) return facet;
        return {
          ...facet,
          isOpen: !facet.isOpen,
        };
      });

      return {...state, facets, activeFilters: getActiveFilters(facets)};
    }

    case `toggle-facet-option`: {
      const facets = state.facets.map((facet) => {
        if (facet.id !== action.payload.facetId) return facet;

        return {
          ...facet,
          options: facet.options.map((option) => {
            if (option.id !== action.payload.optionId) return option;

            return {
              ...option,
              selected: action.payload.selected,
            };
          }),
        };
      });
      return {...state, facets, activeFilters: getActiveFilters(facets)};
    }

    case `apply-filter`: {
      const {facets: facetList} = action.payload;
      const facets = state.facets.map((facet) => {
        return {
          ...facet,
          options: facet.options.map((option) => {
            const matchItem = facetList.find(
              (item) =>
                item.facetId === option.facetId && item.optionId === option.id,
            );
            return {
              ...option,
              selected: matchItem ? matchItem.selected : false,
            };
          }),
        };
      });
      return {...state, facets, activeFilters: getActiveFilters(facets)};
    }

    case `unselected-all-facet-options`: {
      const facets = state.facets.map((facet) => ({
        ...facet,
        options: facet.options.map((option) => ({
          ...option,
          selected: false,
        })),
      }));

      return {
        ...state,
        facets,
        activeFilters: getActiveFilters(facets),
      };
    }

    case `reset-all-facet-options`: {
      const facets = state.facets.map((facet) => ({
        ...facet,
        options: facet.options.map((option) => ({
          ...option,
          selected: Boolean(
            action.payload.options.find((item) => item.id === option.id),
          ),
        })),
      }));

      return {
        ...state,
        facets,
        activeFilters: getActiveFilters(facets),
      };
    }

    default:
      throw Error(`Unknown action`);
  }
};
